import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Search, X, User, ListTodo, FileText, Phone, ArrowRight, Calculator, Wrench, LayoutGrid, Map } from "lucide-react";
import api from "@/lib/api";
import { listContacts } from "@/lib/crmApi";
import { navigateOverlaySafe } from "@/lib/paletteNav";
import { useLanguage } from "@/contexts/LanguageContext";
import { TL } from "@/lib/prekladyUI";
import { odebirejZemi, zemeTenanta } from "@/lib/tenantMena";
import { platiUrlProZemi } from "@/data/calculatorySk";
import { useAuth } from "@/contexts/AuthContext";
import { useMaTym } from "@/lib/mujTym";

/* Rychlé skoky (command palette) — stránky, nástroje, kalkulačky.
 * kw = klíčová slova (CZ + SK zároveň, bezdiakritiky) → aby Slovák našel „mapa", „nastroje",
 *      „kalkulacka", „klienti" apod. label/labelSk = zobrazený název (SK přes labelSk). cat = kategorie. */
const DESTINATIONS = [
  // Stránky
  { label: "Nástěnka", labelSk: "Nástenka", path: "/crm", cat: "Stránka", icon: LayoutGrid, kw: ["nastenka", "dashboard", "prehled", "prehlad", "plocha", "domov", "crm"] },
  { label: "Klienti", labelSk: "Klienti", path: "/klienti", cat: "Stránka", icon: LayoutGrid, kw: ["klienti", "kontakty", "kontakt", "adresar", "klient", "zakaznik", "zakaznici"] },
  { label: "Obchody", labelSk: "Obchody", path: "/obchody", cat: "Stránka", icon: LayoutGrid, kw: ["obchody", "pipeline", "deal", "obchod", "predaj", "predaje", "zakazky"] },
  { label: "Diář", labelSk: "Diár", path: "/diar", cat: "Stránka", icon: LayoutGrid, kw: ["diar", "kalendar", "schuzky", "stretnutia", "stretnutie", "ukoly", "ulohy", "udalosti", "rezervace", "rezervacie", "termin"] },
  { label: "Pošta", labelSk: "Pošta", path: "/posta", cat: "Stránka", icon: LayoutGrid, kw: ["posta", "email", "maily", "gmail", "sprava", "spravy"] },
  { label: "Manažer", labelSk: "Manažér", path: "/manazer", cat: "Stránka", icon: LayoutGrid, kw: ["manazer", "tym", "tim", "poradci", "poradcovia", "manager", "vedenie"] },
  { label: "Nastavení", labelSk: "Nastavenia", path: "/nastaveni", cat: "Stránka", icon: LayoutGrid, kw: ["nastaveni", "nastavenia", "profil", "ucet", "vizitka", "heslo"] },
  { label: "Mapa klientů", labelSk: "Mapa klientov", path: "/mapa-klientu", cat: "Stránka", icon: Map, kw: ["mapa", "mapka", "map", "klienti na mape", "gps", "trasa", "cesta", "geolokace", "poloha"] },
  // Nástroje
  { label: "Finanční analýza", labelSk: "Finančná analýza", path: "/tools", cat: "Nástroj", icon: Wrench, kw: ["analyza", "finmapa", "financna mapa", "schuzka", "stretnutie", "financie", "fip"] },
  { label: "Leadovač (funnely)", labelSk: "Leadovač (funely)", path: "/crm/leadgen", cat: "Nástroj", icon: Wrench, kw: ["leadovac", "lead", "leady", "funnel", "funel", "kampan", "sber", "zber"] },
  { label: "Pošta + AI e-mail", labelSk: "Pošta + AI e-mail", path: "/posta", cat: "Nástroj", icon: Wrench, kw: ["ai", "email", "koncept", "draft"] },
  { label: "Servisní radar", labelSk: "Servisný radar", path: "/crm/servis-radar", cat: "Nástroj", icon: Wrench, kw: ["servis", "radar", "fixace", "fixacia", "vyroci", "vyrocie", "volat"] },
  { label: "Nábor", labelSk: "Nábor", path: "/nabor", cat: "Nástroj", icon: Wrench, kw: ["nabor", "kandidat", "recruit", "poradca", "ziskavanie", "konkurencia"] },
  { label: "Web & funnely", labelSk: "Web & funely", path: "/nastaveni/web", cat: "Nástroj", icon: Wrench, kw: ["web", "stranka", "funnel", "funel", "landing"] },
  { label: "Akademie pro nováčky", labelSk: "Akadémia pre nováčikov", path: "/akademie/produkty", cat: "Nástroj", icon: Wrench, kw: ["akademie", "akademia", "vzdelavani", "vzdelavanie", "skoleni", "skolenie", "novacek", "kurz", "zkouska", "skuska", "certifikat"] },
  { label: "Všechny nástroje", labelSk: "Všetky nástroje", path: "/tools", cat: "Nástroj", icon: Wrench, kw: ["nastroje", "tools", "vse", "vsetko", "katalog"] },
  // Kalkulačky
  { label: "Penzijní kalkulačka", labelSk: "Dôchodková kalkulačka", path: "/penze", cat: "Kalkulačka", icon: Calculator, kw: ["penze", "penzia", "dps", "dip", "penzijko", "duchod", "dochodok", "dochodkova", "stari", "staroba", "kalkulacka", "kalkulacke"] },
  { label: "Životní pojištění", labelSk: "Životné poistenie", path: "/zivotni-pojisteni", cat: "Kalkulačka", icon: Calculator, kw: ["zivotni", "zivotne", "pojisteni", "poistenie", "poistka", "kryti", "krytie", "smrt", "invalidita", "kalkulacka"] },
  { label: "Historická kalkulačka", labelSk: "Historická kalkulačka", path: "/historicka-kalkulacka", cat: "Kalkulačka", icon: Calculator, kw: ["historicka", "vynos", "investice", "investicie", "graf", "sila casu", "kalkulacka"] },
  { label: "Finanční matematika", labelSk: "Finančná matematika", path: "/fin-matematika", cat: "Kalkulačka", icon: Calculator, kw: ["matematika", "uver", "hypoteka", "renta", "splatka", "urok", "kalkulacka"] },
  { label: "Investiční strategie", labelSk: "Investičné stratégie", path: "/investicni-strategie", cat: "Kalkulačka", icon: Calculator, kw: ["strategie", "strategia", "norsky", "dalio", "portfolio", "uroceni", "urocenie", "investice", "investicie", "kalkulacka"] },
  { label: "Investiční studio (DEMO)", labelSk: "Investičné štúdio (DEMO)", path: "/investicni-studio", cat: "Kalkulačka", icon: Calculator, kw: ["studio", "stúdio", "hwm", "high water mark", "vykonnostni poplatek", "vykonnostny poplatok", "krize", "krizy", "naklady", "naklady", "composer", "prezentace", "demo", "investice", "investicie"] },
  { label: "Provizní kalkulačka", labelSk: "Provízna kalkulačka", path: "/provize", cat: "Kalkulačka", icon: Calculator, kw: ["provize", "provizia", "bj", "odmena", "kalkulacka"] },
  { label: "Všechny kalkulačky", labelSk: "Všetky kalkulačky", path: "/calculators", cat: "Kalkulačka", icon: Calculator, kw: ["kalkulacky", "kalkulacka", "vypocty", "vse", "vsetko"] },
];

/* Bezdiakritikové porovnání — „brně" == „brne" == „Brno" (částečná shoda). */
const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/* Telefon se porovnává jen v číslicích a bez předvolby (CRM-13): „777 111"
 * dřív nenašlo uložené „+420777111…". Aspoň 3 číslice, ať „12" nesvítí všude. */
const cisliceTelefonu = (s) => String(s || "").replace(/\D/g, "").replace(/^(00)?(420|421)(?=\d{9}$)/, "");
export const telefonSedi = (telefon, dotaz) => {
  const d = cisliceTelefonu(dotaz);
  if (d.length < 3 || /[a-zá-ž]/i.test(String(dotaz))) return false;
  return cisliceTelefonu(telefon).includes(d);
};

/* Párování měst i přes skloňování — „Brně" (brne) ~ „Brno" (brno) přes společný kmen. */
function cityMatches(cityField, target) {
  const a = norm(cityField), b = norm(target);
  if (!a || !b) return false;
  if (a.includes(b) || b.includes(a)) return true;
  const stem = Math.max(3, Math.min(a.length, b.length) - 2); // odseknout koncovku
  return a.slice(0, stem) === b.slice(0, stem);
}

/* Chytré akce — dotaz podle záměru navrhne rovnou tu správnou stránku/filtr.
 * kw = spouštěcí slova (bezdiakritiky); build(q) → { label, sub, path } nebo null. */
const SMART_ACTIONS = [
  { id: "servis", kw: ["konci", "konci pojisteni", "vyroci", "vyrocie", "fixace", "fixacia", "splatnost", "pojisteni auta", "poistenie auta", "povinne ruceni", "pzp", "obnova", "expirace", "expiracia", "vyprsi", "servis", "radar"],
    build: (lang) => (lang === "sk"
      ? { label: "Servisný radar — zmluvy pred koncom / výročím", sub: "Fixácie, výročia, poistky čo končia — komu volať", path: "/crm/servis-radar", icon: Wrench }
      : { label: "Servisní radar — smlouvy před koncem / výročím", sub: "Fixace, výročí, pojistky co končí — komu volat", path: "/crm/servis-radar", icon: Wrench }) },
  { id: "prilezitost", kw: ["prilezitost", "prilezitosti", "cross", "dokup", "podpojisten", "podpoistene", "chybi", "chyba", "co nabidnout", "co ponuknut", "potencial"],
    build: (lang) => (lang === "sk"
      ? { label: "Radar príležitostí u klientov", sub: "Kde má klient medzeru — čo doriešiť / ponúknuť", path: "/crm/radar-prilezitosti", icon: LayoutGrid }
      : { label: "Radar příležitostí u klientů", sub: "Kde má klient mezeru — co dořešit / nabídnout", path: "/crm/radar-prilezitosti", icon: LayoutGrid }) },
  { id: "bezkontaktu", kw: ["bez kontaktu", "dlouho", "dlho", "zanedban", "zanedbany", "neozval", "stagnace", "stagnacia", "nekontaktovan"],
    build: (lang) => (lang === "sk"
      ? { label: "Klienti bez kontaktu 30+ dní", sub: "Koho treba osloviť", path: "/klienti", icon: LayoutGrid }
      : { label: "Klienti bez kontaktu 30+ dní", sub: "Koho je potřeba oslovit", path: "/klienti", icon: LayoutGrid }) },
  { id: "nabor", kw: ["nabor", "poradce", "poradca", "kandidat", "konkurence", "konkurencia", "cnb", "recruit", "tym", "tim", "ziskavanie"],
    build: (lang) => (lang === "sk"
      ? { label: "Náborová pipeline / hľadanie poradcov", sub: "Kandidáti a konkurenční poradcovia", path: "/nabor", icon: Wrench }
      : { label: "Náborová pipeline / hledání poradců", sub: "Kandidáti a konkurenční poradci", path: "/nabor", icon: Wrench }) },
];

/* Rozpozná v dotazu město (i s předložkou „v/ve/z/ze/na") a kategorii (VIP/A/B/C). */
function parseIntent(rawQuery) {
  const q = norm(rawQuery).trim();
  let cityTarget = null, catTarget = null;
  // „... v brně" / „z prahy" / „klienti brno"
  const cityMatch = q.match(/\b(?:ve?|ze?|na)\s+([a-z]{3,})/);
  if (cityMatch) cityTarget = cityMatch[1];
  // „vip klienti" / „kategorie a"
  if (/\bvip\b/.test(q)) catTarget = "VIP";
  else { const cm = q.match(/\bkategori[ei]?\s+([abc])\b/); if (cm) catTarget = cm[1].toUpperCase(); }
  return { cityTarget, catTarget };
}

/**
 * Global search palette — opens via the magnifier button in the navbar or Cmd/Ctrl+K.
 * Searches contacts (by name/phone/email/city/company/tags), tasks, and nav destinations.
 * Chytré hledání: bezdiakritiky, „klienti v Brně", „VIP klienti", a záměry (pojištění končí →
 * Servisní radar). Výběr klienta otevře složku; výběr úkolu jde na /diar.
 */
export default function GlobalSearch({ showTrigger = false }) {
  const { t, lang } = useLanguage();
  /* Skok „Manažer“ jen tomu, kdo tým má (jako položka v levé liště) — ostatní by
   * /manazer přesměroval zpět na Nástěnku (velký test funkčnosti 24. 10. 2026). */
  const { user } = useAuth();
  const maTymUzivatel = useMaTym(user);
  const sk = lang === "sk";
  const dLabel = (d) => (sk && d.labelSk ? d.labelSk : d.label);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [contacts, setContacts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  // Výpadek serveru ≠ „nic nenalezeno" (CRM-13).
  const [chyba, setChyba] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Cmd/Ctrl + K opens the palette anywhere.
  useEffect(() => {
    const onKey = (e) => {
      // ⌘K (Mac) / Ctrl+K i Alt+K (Windows — Ctrl+K si někdy bere prohlížeč).
      if ((e.metaKey || e.ctrlKey || e.altKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-global-search", onOpenEvent);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("open-global-search", onOpenEvent); };
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
    else { setQuery(""); setContacts([]); setTasks([]); setActiveIdx(0); }
  }, [open]);

  // Debounced fetch
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) { setContacts([]); setTasks([]); setChyba(false); return; }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        let selhalo = false;
        const [cRes, tRes] = await Promise.all([
          // Kontakty z cache crmApi (sdílená s nástěnkou) — ne celý seznam při každém písmenu.
          listContacts().then((data) => ({ data: Array.isArray(data) ? data : [] })).catch(() => { selhalo = true; return { data: [] }; }),
          api.get("/api/crm/tasks").catch(() => { selhalo = true; return { data: [] }; }),
        ]);
        if (cancelled) return;
        setChyba(selhalo);
        const nq = norm(q);
        const tagQuery = nq.replace(/^#/, "");
        const { cityTarget, catTarget } = parseIntent(q);
        const matchedContacts = (cRes.data || []).filter((c) => {
          const tagsArr = Array.isArray(c.tags) ? c.tags : [];
          const tagsHit = tagsArr.some((t) => norm(t).includes(tagQuery));
          if (nq.startsWith("#")) return tagsHit; // strict hashtag-only mode
          // Kategorie-intent („VIP klienti") — případně i s městem
          if (catTarget) {
            const catOk = norm(c.category) === norm(catTarget) || (catTarget === "VIP" && norm(c.category).includes("vip"));
            return cityTarget ? (catOk && cityMatches(c.city, cityTarget)) : catOk;
          }
          // Město-intent („klienti v Brně") — jen podle města (i přes skloňování)
          if (cityTarget) return cityMatches(c.city, cityTarget);
          return (
            tagsHit ||
            norm(c.name).includes(nq) ||
            norm(c.surname).includes(nq) ||
            norm(`${c.name || ""} ${c.surname || ""}`).includes(nq) ||
            telefonSedi(c.phone, q) ||
            norm(c.email).includes(nq) ||
            norm(c.city).includes(nq) ||
            norm(c.company).includes(nq)
          );
        }).slice(0, 12);
        const matchedTasks = (tRes.data || []).filter((t) => norm(t.text).includes(nq)).slice(0, 6);
        setContacts(matchedContacts);
        setTasks(matchedTasks);
        setActiveIdx(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, open]);

  // Chytré akce — dotaz podle záměru navrhne rovnou správnou stránku/filtr.
  const smartActions = useMemo(() => {
    const nq = norm(query);
    if (nq.length < 3) return [];
    const out = [];
    SMART_ACTIONS.forEach((a) => {
      if (a.kw.some((k) => nq.includes(k) || (k.length >= 4 && k.includes(nq)))) {
        const b = a.build(lang); if (b) out.push(b);
      }
    });
    return out.slice(0, 3);
  }, [query, lang]);

  // Rychlé skoky na stránky / nástroje (command palette)
  /* Nástroje jen pro ČR (penzijko DPS/DIP, Fixace) mimo ČR nenabízet (24. 10. 2026). */
  const zeme = useSyncExternalStore(odebirejZemi, zemeTenanta, zemeTenanta);
  const navMatches = useMemo(() => {
    const q = norm(query).trim();
    if (q.length < 2) return [];
    return DESTINATIONS.filter((d) => platiUrlProZemi(d.path, zeme) && (d.path !== "/manazer" || maTymUzivatel) && (
      norm(d.label).includes(q) || norm(d.labelSk).includes(q) ||
      d.kw.some((k) => norm(k).includes(q) || q.includes(norm(k)))
    )).slice(0, 8);
  }, [query, zeme, maTymUzivatel]);

  const flat = useMemo(() => {
    const list = [];
    smartActions.forEach((s) => list.push({ kind: "smart", item: s }));
    navMatches.forEach((d) => list.push({ kind: "nav", item: d }));
    contacts.forEach((c) => list.push({ kind: "contact", item: c }));
    tasks.forEach((t) => list.push({ kind: "task", item: t }));
    return list;
  }, [contacts, tasks, navMatches, smartActions]);

  const choose = (entry) => {
    if (entry.kind === "smart" || entry.kind === "nav") {
      setOpen(false);
      // Overlay-safe: zavři případnou otevřenou klientskou složku, pak naviguj —
      // jinak overlay (z-78) zůstane viset nad cílovou stránkou (Servisní radar apod.).
      navigateOverlaySafe(navigate, entry.item.path);
    } else if (entry.kind === "contact") {
      setOpen(false);
      // Use the drawer event so the user can stay on the current page.
      window.dispatchEvent(new CustomEvent("open-client-folder", { detail: { contactId: entry.item.id } }));
    } else if (entry.kind === "task") {
      setOpen(false);
      // Také overlay-safe — z otevřené složky by /diar jinak zůstal pod overlayem.
      navigateOverlaySafe(navigate, "/diar");
    }
  };

  const onKeyNav = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && flat[activeIdx]) {
      e.preventDefault();
      choose(flat[activeIdx]);
    }
  };

  return (
    <>
      {showTrigger && (
        <button
          onClick={() => setOpen(true)}
          className="ink-board flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-all" style={{ background: "var(--surface)", border: "1px solid var(--line)", color: "var(--muted)", boxShadow: "var(--shadow-card, 0 2px 10px rgba(38,34,26,0.05))" }}
          data-testid="global-search-trigger"
          title={t("auto.globalSearch.hledatCmdCtrlK")}
        >
          <Search size={14} />
          <span className="hidden lg:inline">{t("auto.globalSearch.hledat")}</span>
          <kbd className="hidden lg:inline ml-1 px-1.5 py-0.5 rounded text-[9px]" style={{ background: "var(--bg)", border: "1px solid var(--line)", color: "var(--muted)" }}>⌘K</kbd>
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="ink-board fixed inset-0 z-[120] backdrop-blur-md flex items-start justify-center pt-[10vh] px-4"
            style={{ background: "rgba(38,34,26,0.55)" }}
            data-testid="global-search-modal"
          >
            <motion.div
              initial={{ y: -12, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -12, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-xl rounded-2xl overflow-hidden" style={{ background: "var(--surface)", border: "1px solid var(--line)", boxShadow: "var(--shadow-float, 0 24px 60px rgba(38,34,26,0.3))" }}
            >
              <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, var(--accent) 50%, transparent)" }} />
              <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: "var(--line)" }}>
                <Search size={16} style={{ color: "var(--accent)" }} />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyNav}
                  placeholder={t("auto.globalSearch.hledatKlientaUkolHashtag")}
                  className="flex-1 bg-transparent outline-none text-sm"
                  style={{ color: "var(--ink)" }}
                  data-testid="global-search-input"
                />
                {loading && <span className="text-[10px]" style={{ color: "var(--muted)" }}>…</span>}
                <button onClick={() => setOpen(false)} className="p-1 transition-colors" style={{ color: "var(--muted)" }} aria-label={t("auto.globalSearch.zavrit")}>
                  <X size={14} />
                </button>
              </div>

              <div className="max-h-[60vh] overflow-y-auto p-2">
                {query.trim().length < 2 ? (
                  <Empty onPick={(x) => setQuery(x)} sk={sk} />
                ) : chyba && flat.length === 0 && !loading ? (
                  <div className="px-3 py-6 text-center text-xs" role="alert" style={{ color: "var(--negative-text)" }} data-testid="global-search-chyba">{TL("Hledání se nepodařilo", lang)}</div>
                ) : flat.length === 0 && !loading ? (
                  <div className="px-3 py-6 text-center text-xs" style={{ color: "var(--muted)" }}>{t("auto.globalSearch.zadneVysledkyPro")} „{query}“</div>
                ) : (
                  <>
                    {smartActions.length > 0 && (
                      <SectionLabel>{t("auto.globalSearch.chytreHledani")}</SectionLabel>
                    )}
                    {smartActions.map((s, k) => (
                      <ResultRow
                        key={"smart-" + s.path + s.label}
                        active={activeIdx === k}
                        onClick={() => choose({ kind: "smart", item: s })}
                        onHover={() => setActiveIdx(k)}
                        testid={`gs-smart-${k}`}
                        icon={s.icon || ArrowRight}
                        title={s.label}
                        subtitle={s.sub || (t("auto.globalSearch.otevrit"))}
                      />
                    ))}
                    {navMatches.length > 0 && (
                      <SectionLabel>{TL("Stránky · nástroje · kalkulačky", lang)}</SectionLabel>
                    )/* SK i CZ shodné */}
                    {navMatches.map((d, k) => (
                      <ResultRow
                        key={d.path + d.label}
                        active={activeIdx === smartActions.length + k}
                        onClick={() => choose({ kind: "nav", item: d })}
                        onHover={() => setActiveIdx(smartActions.length + k)}
                        testid={`gs-nav-${d.path.replace(/[^a-z0-9]/gi, "-")}`}
                        icon={d.icon || ArrowRight}
                        title={dLabel(d)}
                        subtitle={d.cat || (t("auto.globalSearch.otevrit"))}
                      />
                    ))}
                    {contacts.length > 0 && (
                      <SectionLabel>Klienti</SectionLabel>
                    )}
                    {contacts.map((c, i) => (
                      <ResultRow
                        key={c.id}
                        active={activeIdx === smartActions.length + navMatches.length + i}
                        onClick={() => choose({ kind: "contact", item: c })}
                        onHover={() => setActiveIdx(smartActions.length + navMatches.length + i)}
                        testid={`gs-contact-${c.id}`}
                        icon={User}
                        title={`${c.name || ""}${c.surname ? " " + c.surname : ""}`.trim()}
                        subtitle={[
                          [c.phone, c.email, c.city].filter(Boolean).join(" · "),
                          (c.tags || []).slice(0, 4).map((t) => `#${t}`).join(" "),
                        ].filter(Boolean).join(" — ")}
                      />
                    ))}
                    {tasks.length > 0 && (
                      <SectionLabel>{t("auto.globalSearch.ukoly")}</SectionLabel>
                    )}
                    {tasks.map((t, j) => (
                      <ResultRow
                        key={t.id}
                        active={activeIdx === smartActions.length + navMatches.length + contacts.length + j}
                        onClick={() => choose({ kind: "task", item: t })}
                        onHover={() => setActiveIdx(smartActions.length + navMatches.length + contacts.length + j)}
                        testid={`gs-task-${t.id}`}
                        icon={ListTodo}
                        title={t.text}
                        subtitle={t.deadline ? `Termín: ${t.deadline}` : ""}
                      />
                    ))}
                  </>
                )}
              </div>

              <div className="px-4 py-2 border-t text-[10px] flex items-center justify-between" style={{ borderColor: "var(--line)", color: "var(--muted)", background: "var(--bg)" }}>
                <span>{t("auto.globalSearch.navigaceOtevritEscZavrit")}</span>
                <span style={{ color: "var(--muted)" }}>{t("auto.globalSearch.beplanGlobalniHledani")}</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="px-3 pt-3 pb-1 text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: "var(--accent)" }}>
      {children}
    </div>
  );
}

function ResultRow({ active, onClick, onHover, icon: Icon, title, subtitle, testid }) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={onHover}
      data-testid={testid}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors"
      style={{ background: active ? "var(--accent-soft)" : "transparent", boxShadow: active ? "inset 0 0 0 1px var(--accent)" : "none" }}
    >
      <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
        style={{ background: active ? "var(--accent-soft)" : "var(--surface-2)", color: active ? "var(--accent)" : "var(--muted)" }}>
        <Icon size={13} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate" style={{ color: "var(--ink)" }}>{title}</div>
        {subtitle && <div className="text-[11px] truncate" style={{ color: "var(--muted)" }}>{subtitle}</div>}
      </div>
      <ArrowRight size={12} className="flex-shrink-0" style={{ color: "var(--muted)" }} />
    </button>
  );
}

function Empty({ onPick, sk }) {
  const examples = sk
    ? ["klienti v Brne", "VIP klienti", "poistenie auta končí", "bez kontaktu", "#hypo"]
    : ["klienti v Brně", "VIP klienti", "pojištění auta končí", "bez kontaktu", "#hypo"];
  return (
    <div className="px-3 py-5 space-y-3">
      <div className="text-xs text-center" style={{ color: "var(--muted)" }}>
        {TL("Hledej klienta, úkol, telefon, #tag — nebo napiš dotaz:")}
      </div>
      <div className="flex flex-wrap justify-center gap-1.5">
        {examples.map((ex) => (
          <button
            key={ex}
            onClick={() => onPick?.(ex)}
            className="px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors"
            style={{ background: "var(--surface-2)", color: "var(--accent)", border: "1px solid var(--line)" }}
            data-testid={`gs-example-${ex.replace(/[^a-z]/gi, "").toLowerCase()}`}
          >
            {ex}
          </button>
        ))}
      </div>
      <div className="flex justify-center gap-3 text-[10px] pt-1" style={{ color: "var(--muted)" }}>
        <span className="flex items-center gap-1"><User size={11} /> Klienti</span>
        <span className="flex items-center gap-1"><ListTodo size={11} /> {TL("Úkoly")}</span>
        <span className="flex items-center gap-1"><Phone size={11} /> {TL("Telefony")}</span>
      </div>
    </div>
  );
}
